/**
 * Password reset landing page — target of the email link Supabase sends
 * (`redirectTo: <origin>/reset-password` in resetPasswordForEmail).
 * Supabase delivers the recovery session in the URL fragment; detectRemoteSession
 * applies it, then the user picks a new password.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Check, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/integrations/supabase/client'
import { initAuth, passwordProblem, updatePassword } from '@/lib/auth/auth'

export default function ResetPasswordPage() {
  const navigate = useNavigate()
  const [ready, setReady] = useState(false)
  const [hasSession, setHasSession] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  // Supabase detects the recovery link's session from the URL fragment on load.
  // Wait briefly for it; give up with a clear "link invalid" state otherwise.
  useEffect(() => {
    initAuth()
    let active = true
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') setHasSession(true)
      if (session) setHasSession(true)
    })
    const timer = setTimeout(() => {
      if (!active) return
      setReady(true)
      subscription.unsubscribe()
    }, 3000)
    return () => {
      active = false
      clearTimeout(timer)
      subscription.unsubscribe()
    }
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const pw = passwordProblem(password)
    if (pw) return setError(pw)
    if (password !== confirmPassword) return setError('Passwords do not match.')
    setBusy(true)
    const { error: err } = await updatePassword(password)
    setBusy(false)
    if (err) return setError(err)
    setDone(true)
  }

  return (
    <div className="min-h-screen bg-[#0e0e12] text-white flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#14141a] p-6 shadow-2xl">
        <h1 className="text-lg font-semibold">Set a new password</h1>
        <p className="mt-1 text-sm text-white/50">
          Choose a new password for your RoadRatings account.
        </p>

        {!hasSession ? (
          ready && (
            <div className="mt-6 space-y-4">
              <div className="flex items-start gap-2 rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                This remote login link is invalid or has expired. Request a new one from the
                Sign In panel on the map page.
              </div>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400"
              >
                Back to the map
              </button>
            </div>
          )
        ) : done ? (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-2 rounded-lg border border-emerald-400/20 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-300">
              <Check className="h-4 w-4" /> Password updated.
            </div>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-amber-400"
            >
              Back to the map
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <Lock className="h-3.5 w-3.5" /> New password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-white/60">
                <Lock className="h-3.5 w-3.5" /> Confirm new password
              </span>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  autoComplete="new-password"
                  className={cn(
                    'w-full rounded-lg border bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-amber-400/60',
                    confirmPassword.length > 0 && confirmPassword !== password
                      ? 'border-red-400/50'
                      : 'border-white/10'
                  )}
                />
                {confirmPassword.length > 0 && confirmPassword === password && (
                  <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" />
                )}
              </div>
              {confirmPassword.length > 0 && confirmPassword !== password && (
                <span className="mt-1 block text-xs text-red-400">Passwords do not match.</span>
              )}
            </label>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs text-red-300">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className={cn(
                'w-full rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-black transition-colors',
                busy ? 'cursor-not-allowed opacity-60' : 'hover:bg-amber-400'
              )}
            >
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
